import {useState} from 'react';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehiclePhotosQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';

import {Nullish, TestIdProps} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {useServiceCasePhotoActions} from '../hooks/useServiceCasePhotoActions';
import {HistoricalServiceCaseAlbum} from '../types/HistoricalServiceCaseAlbum';
import {ServiceCaseAuditData} from '../types/ServiceCaseAuditData';
import {getServiceCasePhotoAlbums} from '../utils/getServiceCasePhotoAlbums';
import {ServiceCasePhotosCard} from './ServiceCasePhotosCard';

interface ServiceCasePhotosCardContainerProps extends TestIdProps {
  vehicleId: string;
  serviceCaseId: string;
  albums: HistoricalServiceCaseAlbum[];
  auditData: ServiceCaseAuditData;
  onVehicleAlbumDownload: (albumId: string) => void;
  onInspectionDownload: (auditId: string | Nullish) => void;
}

export function ServiceCasePhotosCardContainer(props: ServiceCasePhotosCardContainerProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const [isExpanded, setIsExpanded] = useState(false);

  const {data: vehiclePhotos} = useGetVehiclePhotosQuery(
    {vehicleId: props.vehicleId},
    {skip: !isExpanded}
  );

  // Permission scopes are only needed once the card is expanded and the context menu is reachable.
  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId,
    },
    {skip: !isExpanded}
  );
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId}, {skip: !isExpanded});

  const [canDeleteVehicleAlbumPhoto] = usePermissions({
    permissionKeys: ['deleteVehicleAlbumPhoto'],
    scopes: {
      deleteVehicleAlbumPhoto: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const photoActions = useServiceCasePhotoActions({vehicleId: props.vehicleId});

  const albums = getServiceCasePhotoAlbums(props.albums, vehiclePhotos ?? []);

  const handleExpandButtonClick = () => setIsExpanded((prev) => !prev);

  return (
    <ServiceCasePhotosCard
      data-testid={props['data-testid']}
      serviceCase={serviceCase}
      isExpanded={isExpanded}
      onExpandButtonClick={handleExpandButtonClick}
      albums={albums}
      checkinPhotos={props.auditData.checkinPhotos}
      checkinVideos={props.auditData.checkinVideos}
      handoverPhotos={props.auditData.handoverPhotos}
      handoverVideos={props.auditData.handoverVideos}
      canDeletePhotos={canDeleteVehicleAlbumPhoto}
      processingPhotoIds={photoActions.processingPhotoIds}
      onSetCoverPhoto={photoActions.setPhotoAsCover}
      onUnsetCoverPhoto={photoActions.unsetPhotoAsCover}
      onDeletePhoto={photoActions.deleteAlbumPhoto}
      onDownloadAlbum={props.onVehicleAlbumDownload}
      onDownloadCheckin={() => props.onInspectionDownload(props.auditData.checkinAuditId)}
      onDownloadHandover={() => props.onInspectionDownload(props.auditData.handoverAuditId)}
    />
  );
}
